/**
 * The approval queue — where a registration becomes a login, or does not.
 *
 * The decision an approver is actually making is "is this person who they say
 * they are, and should they see that team's revenue". So each card leads with
 * the employee id and the person it resolves to *right now*, re-checked on
 * every load rather than replayed from what was true when the form was
 * submitted. The identity graph is rebuilt nightly; a match made last week can
 * move, and approving a drifted match would attribute one person's territory to
 * another.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, BadgeCheck, Check, Clock, Inbox, ShieldAlert, UserX, X,
} from "lucide-react";

import { api, describeError } from "@/api/client";
import { KpiCard } from "@/components/cards/KpiCard";
import {
  ROLE_LABEL, type AccessRequestRow, type Role,
} from "@/types/api";
import { count } from "@/utils/format";

/** ADMIN is absent on purpose — the backend refuses it from this queue. */
const GRANTABLE: Role[] = ["EMPLOYEE", "MANAGER", "VP", "MANAGEMENT"];

const TABS = [
  { key: "PENDING", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
] as const;

function when(iso: string) {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export function AccessRequests() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("PENDING");

  const { data, isLoading } = useQuery({
    queryKey: ["access-requests", tab],
    queryFn: async () => {
      const { data } = await api.get<{
        results: AccessRequestRow[];
        summary: { pending: number; approved: number; rejected: number };
      }>("/admin/access-requests", { params: { status: tab } });
      return data;
    },
    staleTime: 10_000,
  });

  const rows = data?.results ?? [];
  const summary = data?.summary;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["access-requests"] });
    void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-[24px] tracking-tight">
          Access requests
        </h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
          Registering does not create a login. Approving one does — check the
          employee id resolves to the person you expect before you do.
        </p>
      </div>

      <div className="grid gap-3"
           style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
        <KpiCard loading={isLoading} label="Waiting" value={count(summary?.pending)} />
        <KpiCard loading={isLoading} label="Approved" value={count(summary?.approved)} />
        <KpiCard loading={isLoading} label="Rejected" value={count(summary?.rejected)} />
      </div>

      <div className="flex items-center gap-1 bg-surface-alt rounded-md p-0.5 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1 rounded text-[12.5px] ${
              tab === t.key
                ? "bg-surface shadow-card font-medium text-ink"
                : "text-ink-muted hover:text-ink"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="skeleton h-64" />
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center">
          <Inbox size={22} className="mx-auto text-ink-faint mb-2" />
          <p className="text-sm text-ink-muted">
            {tab === "PENDING" ? "Nothing waiting for approval."
                               : `No ${tab.toLowerCase()} requests.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <RequestCard key={row.id} row={row} onDecided={invalidate} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function RequestCard({ row, onDecided }: {
  row: AccessRequestRow; onDecided: () => void;
}) {
  const [role, setRole] = useState<Role>(
    GRANTABLE.includes(row.requested_role) ? row.requested_role : "EMPLOYEE");
  const [note, setNote] = useState("");
  const [financials, setFinancials] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: async (approve: boolean) => {
      await api.post(`/admin/access-requests/${row.id}/decide`, {
        approve, role, note: note.trim(),
        may_see_financials: financials,
        may_see_patient_pii: false,
        may_export: true,
      });
    },
    onSuccess: onDecided,
    onError: (err) => setError(describeError(err)),
  });

  const pending = row.status === "PENDING";
  const resolution = row.current_resolution;
  const unresolvable = pending && resolution && !resolution.ok;
  const drifted = pending && resolution?.drifted;

  return (
    <article className="card p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="font-display font-bold text-[16px] tracking-tight">
              {row.full_name || row.username}
            </h2>
            <span className="font-mono text-[12px] text-ink-muted">
              {row.username}
            </span>
            <StatusChip status={row.status} />
          </div>
          <p className="text-[12px] text-ink-faint mt-0.5">
            asked for {ROLE_LABEL[row.requested_role]} · {when(row.requested_at)}
            {row.email ? ` · ${row.email}` : ""}
            {row.requested_ip ? ` · from ${row.requested_ip}` : ""}
          </p>
        </div>
      </div>

      {/* --- identity, which is the actual decision ------------------------ */}
      <div className={`rounded-md border px-3 py-2.5 ${
        unresolvable ? "border-bad/30 bg-bad-soft"
        : drifted ? "border-warn/30 bg-warn-soft"
        : "border-rule bg-surface-alt"}`}>
        <div className="flex items-start gap-2">
          {unresolvable ? <ShieldAlert size={14} className="mt-0.5 shrink-0 text-bad" />
            : drifted ? <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warn" />
            : <BadgeCheck size={14} className="mt-0.5 shrink-0 text-good" />}
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="label">employee id</span>
              <span className="font-mono text-[13px]">{row.employee_id || "none given"}</span>
            </div>

            {unresolvable ? (
              <p className="text-[12.5px] text-bad">
                Does not resolve to anyone now: {resolution.reason} Approving
                would create a login with no hierarchy behind it.
              </p>
            ) : (
              <>
                <p className="text-[13px]">
                  {resolution?.name || row.resolved_name || "—"}
                  {(resolution?.designation || row.resolved_designation) && (
                    <span className="text-ink-muted">
                      {" "}· {resolution?.designation || row.resolved_designation}
                    </span>
                  )}
                </p>
                <p className="text-[11.5px] text-ink-faint">
                  person{" "}
                  <span className="font-mono">
                    {resolution?.person_key || row.resolved_person_key || "—"}
                  </span>
                  {resolution?.method ? ` · matched by ${resolution.method}` : ""}
                </p>
                {!!resolution?.also_known_as?.length && (
                  <p className="text-[11.5px] text-ink-faint">
                    also recorded as{" "}
                    <span className="font-mono">
                      {resolution.also_known_as.join(", ")}
                    </span>
                  </p>
                )}
              </>
            )}

            {drifted && (
              <p className="text-[12.5px] text-warn">
                This id resolved to <strong>{row.resolved_name}</strong> when the
                request was made and to <strong>{resolution?.name}</strong> now —
                the nightly identity rebuild moved it. Confirm which is right
                before approving.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* --- decision ------------------------------------------------------ */}
      {pending ? (
        <div className="space-y-2.5">
          <div className="flex flex-wrap gap-3 items-end">
            <label className="flex flex-col gap-1">
              <span className="label">Grant role</span>
              <select value={role} onChange={(e) => setRole(e.target.value as Role)}
                      className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5">
                {GRANTABLE.map((r) => (
                  <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-[12.5px] pb-1.5">
              <input type="checkbox" checked={financials} className="accent-petrol"
                     onChange={(e) => setFinancials(e.target.checked)} />
              May see financials
            </label>
            <span className="text-[11.5px] text-ink-faint pb-1.5">
              Administrator rights cannot be granted here.
            </span>
          </div>

          <input
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="note for the audit log — why you approved or rejected"
            className="w-full text-sm bg-surface border border-rule rounded-md px-2.5 py-1.5"
          />

          {error && (
            <p className="text-[12.5px] text-bad bg-bad-soft border border-bad/25
                          rounded-md px-3 py-2">{error}</p>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => decide.mutate(true)}
              disabled={decide.isPending || !!unresolvable}
              title={unresolvable
                ? "The employee id does not resolve — this cannot be approved"
                : "Create the login"}
              className="btn btn-primary disabled:opacity-50"
            >
              <Check size={14} /> Approve as {ROLE_LABEL[role]}
            </button>
            <button
              onClick={() => decide.mutate(false)}
              disabled={decide.isPending}
              className="btn disabled:opacity-50"
            >
              <X size={14} /> Reject
            </button>
          </div>
        </div>
      ) : (
        <p className="text-[12.5px] text-ink-muted flex items-center gap-1.5">
          <Clock size={12} className="text-ink-faint" />
          {row.status === "APPROVED" ? "Approved" : "Rejected"}
          {row.decided_at ? ` ${when(row.decided_at)}` : ""}
          {row.decided_by_username ? ` by ${row.decided_by_username}` : ""}
          {row.decision_note ? ` — "${row.decision_note}"` : ""}
        </p>
      )}
    </article>
  );
}

function StatusChip({ status }: { status: AccessRequestRow["status"] }) {
  const style = status === "APPROVED"
    ? { color: "#2F6B3C", bg: "#E7F1E9", Icon: Check }
    : status === "REJECTED"
      ? { color: "#9E3B2E", bg: "#FBECE9", Icon: UserX }
      : { color: "#8A6410", bg: "#FBF3E0", Icon: Clock };
  return (
    <span className="chip border-transparent"
          style={{ color: style.color, background: style.bg }}>
      <style.Icon size={10} /> {status.toLowerCase()}
    </span>
  );
}
